import Guid from "_shared/guids/guid";
import Comment from "work/proposals/comments/comment";

/**
 * The id of the oldest comment the reader `userId` has not read: where their unread reading begins.
 *
 * The Comments pane lists oldest first, bottom-up, by the moment each comment entered the conversation
 * (`sortCommentsOldestFirst` in `proposal-comment-list.tsx`), so this is the topmost unread row, with
 * every newer unread comment below it. "Unread" is `Comment.isUnreadBy`, the same rule the row's dot
 * and the mark-read write use, so the stop cannot scroll to a comment the pane draws as read.
 *
 * `isExternal` narrows it to one conversation, as `CommentThread.isExternal` names them: each tab of
 * the pane is a stop of its own (#5824), and lists only its own conversation.
 */
export function oldestUnreadCommentId(
  comments: readonly Comment[] | undefined,
  userId: Guid | undefined,
  isExternal?: boolean,
): string | undefined {
  let oldest: Comment | undefined;
  for (const comment of comments ?? []) {
    if (!comment.id || !comment.isUnreadBy(userId)) continue;
    if (isExternal !== undefined && comment.thread.isExternal !== isExternal) continue;
    // `<=` rather than `<`: the list's sort is stable, so of two comments that entered the conversation
    // in the same instant — ordinary for comments posted in quick succession, which share a server
    // timestamp — the one later in the store is drawn lower.
    if (!oldest || enteredAt(comment) <= enteredAt(oldest)) oldest = comment;
  }
  return oldest?.id?.value;
}

function enteredAt(comment: Comment): number {
  // The list's own fallback: a comment with no moment yet is the author's own, still on the wire, and
  // never unread, so it can never be taken for the oldest.
  return comment.enteredConversationAt?.valueOf() ?? Number.POSITIVE_INFINITY;
}
